import canvas from '@classes/canvas'
import { getPropertyValue } from '@constants/utils'
import Logger from '@logger'
import { degreesToRadians } from '@turf/helpers'
import { isNumber } from 'lodash'

import { divideIfNotZero, getWindDirection } from '../helper/canvasDraw'
import type { MainParams, StyleParams } from '../Meter'
import type { GaugeDrawResult } from './gauge_types'

const logger = Logger.create('Gauges/Wind')

const getWindAngle = (windDirection: number, maxValue?: number) =>
  divideIfNotZero(Number(windDirection), Number(maxValue)) * 100

const getFromValue = (prevValue?: number, windDirection?: number, maxValue?: number) => {
  if (!prevValue) {
    return windDirection ? getWindAngle(windDirection, maxValue) : 0
  }

  return divideIfNotZero(Number(prevValue), Number(maxValue)) * 100
}

const getToValue = (value?: number | object, maxValue?: number, prevValue?: number, windDirection?: number) => {
  if (!prevValue) {
    return windDirection ? getWindAngle(windDirection, maxValue) : 0
  }

  return divideIfNotZero(Number(value), Number(maxValue)) * 100
}

export function drawWindGauge (windSpeed: number = 0, windSpeedUnit: string = '', main: MainParams, style: StyleParams, windDirection?: number): GaugeDrawResult {
  const { prevValue, maxValue, value, center, radius, ctx, dpx, redraw, formatter, unit, currentValue } = main
  const { arcColor, color } = style

  let newCurrentValue: number = currentValue

  const dd = degreesToRadians(270 / 100)
  const startAngle = degreesToRadians(135)
  const endAngle = degreesToRadians(270 + 135)
  const from = getFromValue(prevValue, windDirection, maxValue)
  const to = getToValue(value, maxValue, prevValue, windDirection)

  const drawing = (current: number) => {
    newCurrentValue = Number(Number(maxValue) * (current / 100))

    const polygonWidthInDegrees = 4
    const currentAngle = startAngle + dd * current
    const polygonAngle = newCurrentValue
    const a = canvas.xy(polygonAngle, center, radius - dpx * 20)
    const b = canvas.xy(polygonAngle - polygonWidthInDegrees, center, radius - dpx * 5)
    const c = canvas.xy(polygonAngle + polygonWidthInDegrees, center, radius - dpx * 5)
    const progressAngle = currentAngle > endAngle ? endAngle : currentAngle

    logger.debug(`progressAngle=${progressAngle}`)

    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    redraw()

    const array: number[] = [192.5, 347.5, 12.5, 167.5, 45, 135, 225, 315]
    array.forEach((angle: number) => {
      const start = canvas.xy(angle, center, angle % 45 === 0 ? radius - dpx : radius)
      const end = canvas.xy(angle, center, radius - dpx * 4)
      canvas.drawLine(ctx, start.x, start.y, end.x, end.y, arcColor, dpx + 1)
    })

    canvas.drawArc(
      ctx,
      center,
      center,
      radius - dpx,
      (Math.PI * (array[1] - 115)) / 180,
      (Math.PI * (array[1] - 93)) / 180,
      getPropertyValue('--gauge-wind1-color'),
      dpx + 1
    )
    canvas.drawArc(
      ctx,
      center,
      center,
      radius - dpx,
      (Math.PI * (array[2] - 87)) / 180,
      (Math.PI * (array[2] - 65)) / 180,
      getPropertyValue('--gauge-wind2-color'),
      dpx + 1
    )

    if (isNumber(windSpeed)) {
      const wind = windSpeed.toFixed()
      const speed = wind ? ctx.measureText(wind.toString()) : ctx.measureText('0')
      canvas.drawValue(
        ctx,
        center - (Number(speed.width) / wind.toString().length - 1),
        center - 10 * dpx,
        formatter(windSpeed || 0),
        14 * dpx,
        color
      )
      if (windSpeedUnit) {
        canvas.drawUnit(
          ctx,
          center + Number(speed.width) / 2 + dpx / 4,
          center - 9 * dpx,
          windSpeedUnit,
          dpx * 9,
          color
        )
      }
    }

    canvas.drawValue(
      ctx,
      center,
      center + 14 * dpx,
      `${formatter(newCurrentValue)} ${unit}${getWindDirection(newCurrentValue)}`,
      14 * dpx,
      color
    )
    canvas.drawLine(ctx, -10, center, radius * 2 + 10, center, arcColor)
    canvas.drawLine(ctx, radius, center, radius * 2 + 5 * dpx, center, arcColor)

    canvas.drawArc(
      ctx,
      center,
      center,
      radius - dpx * 4,
      (Math.PI * (array[0] - 90)) / 180,
      (Math.PI * (array[1] - 90)) / 180,
      arcColor,
      dpx + 1
    )
    canvas.drawArc(
      ctx,
      center,
      center,
      radius - dpx * 4,
      (Math.PI * (array[2] - 90)) / 180,
      (Math.PI * (array[3] - 90)) / 180,
      arcColor,
      dpx + 1
    )

    canvas.drawPolygon(ctx, a, b, c, color)
  }

  const animateId = canvas.animate(from, to, drawing, 1, 'wind')

  return { animateId, newCurrentValue }
}
